/**
 * @module PasswordPage
 *
 * @description Password Page component for the app.
 *
 * Supports
 * - Store passwords - Config from storeData.passwordConfig
 * - Scene Passwords - componentConfig.scenePasswords...
 *
 */

import React from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import Cookies from 'universal-cookie';
import { validatePassword } from './utils/apiUtils';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import './App.scss';

const cookies = new Cookies();

/**
 * @typedef PageStylingProps
 * @desc Props to customize the page
 * @property {String} [passwordButtonCopy=Submit] Button copy for password submit button
 * @property {String} [inputPlaceholder=Password] Placeholder for password input box.
 * @property {String} pwLabel Label for password input box.
 * @property {String} header Header string, can be HTML
 * @property {String} subHeader Sub header string, can be HTML
 * @property {String} body body string, can be HTML
 * @property {String} midSection mid section string, can be HTML
 * @property {String} subSection sub section string, can be HTML
 * @property {String} footer footer string, can be HTML
 * @property {Object} passwordPageStyle React compatible styling object
 * @property {Object} passwordButtonStyle React compatible styling object
 * @property {Object} passwordCardStyle React compatible styling object
 */

/**
 *
 * @typedef PasswordPageProps
 * @desc props reqired by the component
 * @property {('scene'|'store')} mode - Mode, one of scene or store. These are the only supported modes.
 * @property {PageStylingProps} pageStyling
 * @property {Function} setPasswordEnabled - Callback to enable/disable password page
 * @property {String} sceneId Scene ID, *required* for `mode==='scene'`
 * @property {String} scenePassword ScenePassword, *required* for `mode==='scene'`
 *
 */

/**
 *
 * @param {PasswordPageProps} props see type definition for required props
 * @return {JSX.Element}
 */
const PasswordPage = (props) => {
	const {
		mode,
		setPasswordEnabled,
		scenePassword = '',
		sceneId = '',
		passwordPageStyle,
		passwordCardStyle,
		header,
		subHeader,
		body,
		inputPlaceholder,
		pwLabel,
		midSection,
		subSection,
		passwordButtonStyle,
		passwordButtonCopy,
		footer,
	} = props;

	/**
	 * @method onFormSubmit
	 *
	 * @desc
	 * Form submit handler
	 * - If mode === store - validate using API
	 * - If mode === scene - validate from `scenePassword` prop
	 *
	 * Triggered on form change event
	 * @param {Event} e
	 * @return {Promise<void>}
	 */
	const onFormSubmit = async (e) => {
		e.preventDefault();

		const passwordInput = e.target.elements
			.namedItem('password-input')
			.value.trim();

		if (mode === 'scene') {
			if (passwordInput === scenePassword) {
				cookies.set(`scene-password-${sceneId}`, true, {
					maxAge: 3600000,
				});
				setPasswordEnabled(false);
				return;
			}
			alert('Incorrect Password');
			return;
		} else if (mode === 'store') {
			const valid = await validatePassword(passwordInput);
			if (!valid) {
				alert('Incorrect Password');
				return;
			}
			cookies.set('obsess-dev-cookie', true, { maxAge: 3600000 });
			setPasswordEnabled(false);
		}
	};

	return (
		<div
			className={`password-container ${
				scenePassword && 'is-scene'
			} vh-100 vw-100 d-flex justify-content-center align-items-center`}
			style={passwordPageStyle || { background: 'black' }}
		>
			<Card
				className="password-card d-flex p-4"
				style={passwordCardStyle}
			>
				{header && parse(header)}
				{subHeader && parse(subHeader)}
				{body && parse(body)}
				<Form onSubmit={onFormSubmit}>
					<Form.Group className="mb-1">
						{pwLabel && parse(pwLabel)}
						<Form.Control
							className="password-input border-dark pt-2 pb-2"
							id="password-input"
							type="password"
							placeholder={inputPlaceholder ?? 'Password'}
						/>
					</Form.Group>
					{midSection && parse(midSection)}
					<Button
						type="submit"
						variant="primary"
						className="password-button btn-dark mt-3 p-2 w-100 fw-light"
						style={passwordButtonStyle}
					>
						{passwordButtonCopy || 'Submit'}
					</Button>
				</Form>
				{subSection && parse(subSection)}
				{footer && parse(footer)}
			</Card>
		</div>
	);
};

PasswordPage.propTypes = {
	mode: PropTypes.oneOf(['store', 'scene']).isRequired,
	setPasswordEnabled: PropTypes.func.isRequired,
	scenePassword: PropTypes.string,
	sceneId: PropTypes.string,
	passwordPageStyle: PropTypes.object,
	passwordCardStyle: PropTypes.object,
	header: PropTypes.string,
	subHeader: PropTypes.string,
	body: PropTypes.string,
	inputPlaceholder: PropTypes.string,
	pwLabel: PropTypes.string,
	midSection: PropTypes.string,
	subSection: PropTypes.string,
	passwordButtonStyle: PropTypes.object,
	passwordButtonCopy: PropTypes.string,
	footer: PropTypes.string,
};

PasswordPage.defaultProps = {
	mode: 'store',
	setPasswordEnabled: () => { },
	scenePassword: '',
	sceneId: '',
	passwordPageStyle: {},
	passwordCardStyle: {},
	header: '',
	subHeader: '',
	body: '',
	inputPlaceholder: undefined,
	pwLabel: '',
	midSection: '',
	subSection: '',
	passwordButtonStyle: {},
	passwordButtonCopy: '',
	footer: '',
};

export default PasswordPage;
